import React from 'react';
import { StandardTableActions, ColoredBadge } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Lead } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import { EntityAttachmentsSection } from '../../components/EntityAttachmentsSection';

// Currency formatting utility
const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null || isNaN(amount)) return '-';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Leads',
    description: 'Manage your leads database',
    permissionModuleKey: 'leads',
    route: {
      list: '/leads',
      new: '/leads/new',
      detail: (id: number) => `/leads/${id}`,
      edit: (id: number) => `/leads/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-leads',
      detail: (id: number) => `/crm-leads/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search leads by description, client, or assigned user...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientId', 'leadAssignedTo', 'leadStatusId', 'leadContactedDate'
      ],
    },
    {
      name: 'financial',
      label: 'Financial',
      icon: '💰',
      fields: [
        'leadValue', 'leadProfit'
      ],
    },
    {
      name: 'details',
      label: 'Details',
      icon: '📋',
      fields: [
        'leadDescr'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: Lead) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'leadAssignedTo',
        label: 'Assigned To',
        placeholder: 'All Users',
        type: 'select' as const,
        getValue: (item: Lead) => 
          item.leadAssignedToUser
            ? (item.leadAssignedToUser.userDisplayName || item.leadAssignedToUser.userFirstName || `User #${item.leadAssignedToUser.id}`)
            : (item.leadAssignedTo ? `User #${item.leadAssignedTo}` : ''),
        options: [],
      },
      {
        key: 'leadStatusId',
        label: 'Status',
        placeholder: 'All Statuses',
        type: 'select' as const,
        getValue: (item: Lead) => 
          item.leadStatusDescr?.as_descr || (item.leadStatusId ? `Status #${item.leadStatusId}` : ''),
        options: [],
      },
      {
        key: 'leadContactedDate',
        label: 'Contacted Date',
        placeholder: 'All Contacted Date',
        type: 'date' as const,
        getValue: (item: Lead) =>
          item.leadContactedDate
            ? formatDateGlobal(item.leadContactedDate)
            : undefined,
        options: [],
      },
      {
        key: 'leadValue',
        label: 'Invoice Value',
        placeholder: 'All Invoice Values',
        type: 'number' as const,
        getValue: (item: Lead) => item.leadValue?.toString(),
        options: [],
      },
      {
        key: 'leadProfit',
        label: 'Profit',
        placeholder: 'All Profits',
        type: 'number' as const,
        getValue: (item: Lead) => item.leadProfit?.toString(),
        options: [],
      },
      {
        key: 'leadDescr',
        label: 'Description',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Lead) => item.leadDescr,
        options: [],
      },
    ],
    applyFilter: (
      items: Lead[],
      filters: {
        clientId: string[];
        leadAssignedTo: string[];
        leadStatusId: string[];
        leadContactedDate: string[];
        leadValue: string[];
        leadProfit: string[];
        leadDescr: string[] | string;
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client filter
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Assigned To filter
        if (filters.leadAssignedTo && filters.leadAssignedTo.length > 0) {
          const displayValue = item.leadAssignedToUser
            ? (item.leadAssignedToUser.userDisplayName || item.leadAssignedToUser.userFirstName || `User #${item.leadAssignedToUser.id}`)
            : (item.leadAssignedTo ? `User #${item.leadAssignedTo}` : '');
          const matches = filters.leadAssignedTo.some((filter) => {
            return displayValue === filter ||
                   (item.leadAssignedTo && filter === `User #${item.leadAssignedTo}`) ||
                   (item.leadAssignedToUser?.userDisplayName && filter === item.leadAssignedToUser.userDisplayName) ||
                   (item.leadAssignedToUser?.id && filter === `User #${item.leadAssignedToUser.id}`);
          });
          if (!matches) return false;
        }

        // Status filter
        if (filters.leadStatusId && filters.leadStatusId.length > 0) {
          const displayValue = item.leadStatusDescr?.as_descr || (item.leadStatusId ? `Status #${item.leadStatusId}` : '');
          const matches = filters.leadStatusId.some((filter) => {
            return displayValue === filter ||
                   (item.leadStatusId && filter === `Status #${item.leadStatusId}`) ||
                   (item.leadStatusDescr?.as_descr && filter === item.leadStatusDescr.as_descr);
          });
          if (!matches) return false;
        }

        // Contacted Date filter
        if (filters.leadContactedDate.length > 0) {
          if (ranges.leadContactedDate) {
            const range = ranges.leadContactedDate;
            if (range.start && range.end && item.leadContactedDate) {
              const itemDate = new Date(item.leadContactedDate);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.leadContactedDate
              ? formatDateGlobal(item.leadContactedDate)
              : '';
            const matches = filters.leadContactedDate.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Invoice Value filter
        if (filters.leadValue && filters.leadValue.length > 0) {
          const value = item.leadValue?.toString() || '';
          const matches = filters.leadValue.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Profit filter
        if (filters.leadProfit && filters.leadProfit.length > 0) {
          const value = item.leadProfit?.toString() || '';
          const matches = filters.leadProfit.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Description filter (text)
        const descrFilter = filters.leadDescr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.leadDescr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.leadDescr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Toolbar Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Lead',
        showTitle: false,
        variant: 'primary' as const,
        href: '/leads/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Lead) => {
      window.location.href = `/leads/${item.leadId}`;
    },
    keyExtractor: (item: Lead) => item.leadId,
    getClientId: (item: Lead) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new lead.',
    noSearchResults: 'No leads found matching your search criteria.',
    noFilterResults: 'No leads match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Lead',
    message: 'Are you sure you want to delete this lead? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete lead. Please try again.',
    handler: async (id: number) => {
      const { deleteLead } = await import('../../services/mutations');
      await deleteLead(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
    formatCurrency,
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Lead Details',
    notFoundTitle: 'Lead not found',
    notFoundMessage: 'The lead you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Leads',
    defaultTitle: 'Unnamed Lead',
    getTitle: (item: Lead) => {
      return item.leadDescr || `Lead #${item.leadId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Lead',
      subtitle: 'Add a new lead to your database',
      submitText: 'Create Lead',
    },
    edit: {
      title: 'Edit Lead',
      submitText: 'Update Lead',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading lead...',
    errorMessage: 'Failed to save lead. Please try again.',
    supportsFiles: true,
    create: async (data: Partial<Lead>, files?: File[], filesToDetach?: number[]) => {
      const { createLead } = await import('../../services/mutations');
      return await createLead(data, files);
    },
    update: async (id: number, data: Partial<Lead>, files?: File[], filesToDetach?: number[]) => {
      const { updateLead } = await import('../../services/mutations');
      return await updateLead(id, data, files, filesToDetach);
    },
    getViewFileUrl: (entityId: number, fileId: number) =>
      `/crm-leads/leads/${entityId}/files/${fileId}`,
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client',
      required: true,
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'leadStatusId',
      label: 'Status',
      type: 'select',
      placeholder: 'Select status',
      required: true,
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-app-status', // API endpoint to fetch all status options
      labelField: 'as_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'leadAssignedTo',
      label: 'Assigned To',
      type: 'select',
      required: true,
      placeholder: 'Select user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'leadContactedDate',
      label: 'Contacted Date',
      type: 'date',
      required: true,
      placeholder: 'Select contacted date',
      validation: {
        required: true,
      },
    },
    {
      name: 'leadValue',
      label: 'Invoice Value',
      type: 'number',
      placeholder: 'Enter invoice value',
      prefix: <span className="text-gray-500 dark:text-gray-400">€</span>,
      validation: {
        min: 0,
      },
      step: 0.01,
    },
    {
      name: 'leadProfit',
      label: 'Profit',
      type: 'number',
      placeholder: 'Enter profit',
      prefix: <span className="text-gray-500 dark:text-gray-400">€</span>,
      validation: {
        min: 0,
      },
      step: 0.01,
    },
    {
      name: 'leadDescr',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter lead description',
      rows: 4,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'leadNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter lead notes',
      rows: 5,
      validation: {
        maxLength: 5000,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration
  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📋',
        gridCols: 'md:grid-cols-2',
        fields: ['clientId', 'leadStatusId', 'leadAssignedTo', 'leadContactedDate'],
        collapsible: true,
      },
      {
        id: 'financial',
        title: 'Financial Information',
        icon: '💰',
        gridCols: 'md:grid-cols-2',
        fields: ['leadValue', 'leadProfit'],
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Lead Details',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['leadDescr', 'leadNotes'],
        fieldSpans: {
          leadDescr: 'md:col-span-12',
          leadNotes: 'md:col-span-12',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

const renderLeadStatusBadge = (status?: string, color?: string) => (
  status ? <ColoredBadge label={status} color={color} /> : null
);

// Helper function to get columns
export const getColumns = (
  formatDate: (dateString?: string) => string,
  colors?: ThemeColors
): Column<Lead>[] => {
  const textColor = colors?.text || 'text-gray-900';
  const textSecondary = colors?.textSecondary || 'text-gray-700';
  const textMuted = colors?.textMuted || 'text-gray-500';
  
  return [
    {
      key: 'leadAssignedTo',
    header: 'ASGN',
      sortable: true,
      render: (value, row) => (
        row.leadAssignedToUser?.userDisplayName || row.leadAssignedToUser?.userFirstName || (value ? `User #${value}` : '-')
      ),
    },
    {
      key: 'leadStatusId',
      header: 'Status',
      sortable: true,
      render: (value, row) => {
        const status = row.leadStatusDescr?.as_descr;
        if (!status) return '-';
        
        void colors;
        return renderLeadStatusBadge(status, row.leadStatusDescr?.as_color);
      },
    },
    {
      key: 'client.clientName',
      header: 'Client',
      sortable: true,
      render: (value, row) => (
        <TruncatedText
          text={row.client?.clientName || '-'}
          maxLength={50}
          showTooltip={true}
        />
      ),
    },
    {
      key: 'leadDescr',
      header: 'Description',
      sortable: true,
      render: (value) => (
        <TruncatedText
          text={value || ''}
          maxLength={100}
          showTooltip={true}
        />
      ),
    },
    {
      key: 'FileAttachment',
      header: 'ATT',
      sortable: false,
      render: (value, row) => {
        const count = row.FileAttachment?.length ?? 0;
        if (count === 0) return <span className="text-sm text-gray-500">-</span>;
        return <span className="text-sm">{count} file{count !== 1 ? 's' : ''}</span>;
      },
    },
    {
      key: 'leadValue',
      header: 'Invoice',
      sortable: true,
      render: (value) => (
        formatCurrency(value)
      ),
    },
    {
      key: 'leadProfit',
      header: 'Profit',
      sortable: true,
      render: (value) => (
        formatCurrency(value)
      ),
    },
    {
      key: 'leadContactedDate',
      header: 'Contacted Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    {
      key: 'leadCreatedAt',
      header: 'Created At',
      sortable: true,
      render: (value) => formatDate(value),
    },
  ];
};

// Helper function to get detail sections
export const getDetailSections = (
  item: Lead,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : 'None'),
        },
        {
          label: 'Status',
          value: (() => {
            const status = item.leadStatusDescr?.as_descr;
            if (!status) return '-';
            return renderLeadStatusBadge(status, item.leadStatusDescr?.as_color);
          })(),
        },
        {
          label: 'Assigned To',
          value: item.leadAssignedToUser
            ? (item.leadAssignedToUser.userDisplayName || item.leadAssignedToUser.userFirstName || `User #${item.leadAssignedToUser.id}`)
            : (item.leadAssignedTo ? `User #${item.leadAssignedTo}` : 'Unassigned'),
        },
        {
          label: 'Contacted Date',
          value: formatDate(item.leadContactedDate),
        },
      ],
    },
    {
      title: 'Financial Information',
      icon: '💰',
      fields: [
        {
          label: 'Invoice Value',
          value: formatCurrency(item.leadValue),
        },
        {
          label: 'Profit',
          value: formatCurrency(item.leadProfit),
        },
      ],
    },
    {
      title: 'Lead Details',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.leadDescr || '-',
        },
        {
          label: 'Notes',
          value: item.leadNotes || '-',
        },
      ],
    },
    {
      title: 'System Information',
      icon: '⚙️',
      fields: [
        {
          label: 'Created At',
          value: formatDate(item.leadCreatedAt),
        },
        {
          label: 'Created By',
          value: item.leadCreatedUser?.userFirstName || (item.leadCreatedBy ? `User #${item.leadCreatedBy}` : '-'),
        },
        {
          label: 'Updated At',
          value: formatDate(item.leadUpdatedAt),
        },
        {
          label: 'Updated By',
          value: item.leadUpdatedUser?.userFirstName || (item.leadUpdatedBy ? `User #${item.leadUpdatedBy}` : '-'),
        },
      ],
    },
    {
      title: 'Attachments',
      icon: '📎',
      fields: [
        {
          label: '',
          value: React.createElement(EntityAttachmentsSection, {
            downloadEndpoint: `/crm-leads/leads/${item.leadId}/files`,
            files: item.FileAttachment || [],
            defaultDownloadName: `lead_${item.leadId}_files.zip`,
            getViewFileUrl: (fileId) => `/crm-leads/leads/${item.leadId}/files/${fileId}`,
          }),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: Lead) => {
  const status = item.leadStatusDescr?.as_descr;
  if (!status) return null;
  
  return (
    <>
      {renderLeadStatusBadge(status, item.leadStatusDescr?.as_color)}
    </>
  );
};

// Helper function to get toolbar icons
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'kanban':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to get table actions
export const getTableActions = (
  item: Lead,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.leadId)}
      editTo={moduleConfig.module.route.edit(item.leadId)}
      onDelete={() => onDelete(item.leadId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<Lead> => {
  return {
    clientId: undefined,
    leadAssignedTo: undefined,
    leadStatusId: undefined,
    leadContactedDate: undefined,
    leadDescr: '',
    leadNotes: '',
    leadValue: undefined,
    leadProfit: undefined,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Lead): Partial<Lead> => {
  return {
    clientId: item.clientId,
    leadAssignedTo: item.leadAssignedTo,
    leadStatusId: item.leadStatusId,
    leadContactedDate: item.leadContactedDate,
    leadDescr: item.leadDescr || '',
    leadNotes: item.leadNotes || '',
    leadValue: item.leadValue,
    leadProfit: item.leadProfit,
  };
};

